import fp from 'lodash/fp';
import { createAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { PERKS_PAYMENT_OPTIONS } from 'common/constants/perks';

const STATE_PREFIX = 'cart';

const initialState = {
    amountLineItems: [],
    perkLineItems: [],
    showPopup: false,
    showValidationErrors: false,
    validationErrors: {},
};

const cart = createSlice({
    name: STATE_PREFIX,
    initialState,
    reducers: {
        updateAmountLineItem(state, { payload: amount }) {
            const parsedAmount = parseInt(amount, 10);
            if (!state.amountLineItems.length) {
                state.amountLineItems.push({ amount: parsedAmount });
            } else {
                state.amountLineItems[0].amount = parsedAmount;
            }
        },
        addPerkLineItem(state, { payload: perkData }) {
            state.perkLineItems.unshift({ ...perkData });
        },
        addPerk(state, { payload: perkData }) {
            const { perkId } = perkData;
            const perkAlreadyAdded = state.perkLineItems.some(
                li => li.perkId === perkId,
            );
            if (!perkAlreadyAdded) {
                state.perkLineItems.unshift({ ...perkData, quantity: 1 });
            } else {
                state.perkLineItems = state.perkLineItems.map(li => ({
                    ...li,
                    quantity:
                        li.quantity +
                        (li.perkId === perkId &&
                        (!li.limit || li.quantity < li.limit)
                            ? 1
                            : 0),
                }));
            }
        },
        addNewPerk(state, { payload: perkData }) {
            const { perkId } = perkData;
            const perkAlreadyAdded = state.perkLineItems.some(
                li => li.perkId === perkId,
            );
            if (!perkAlreadyAdded) {
                state.perkLineItems.unshift({ ...perkData, quantity: 1 });
            }
        },
        removePerk(state, { payload: perkId }) {
            state.perkLineItems = state.perkLineItems
                .map(li => ({
                    ...li,
                    quantity: li.quantity + (li.perkId === perkId ? -1 : 0),
                }))
                .filter(li => li.quantity);
        },
        removePerkLineItem(state, { payload: perkId }) {
            state.perkLineItems = state.perkLineItems.filter(
                li => li.perkId !== perkId,
            );
        },
        removeAmountLineItems(state) {
            state.amountLineItems = [];
        },
        updatePerkLineItemAmounts(state, { payload: amounts }) {
            const lineItemAmountsMap = fp.flow(
                fp.groupBy('perkId'),
                fp.mapValues(v => (v.length ? v[0] : null)),
            )(amounts);
            const updatedLineItems = state.perkLineItems.map(li => {
                if (!lineItemAmountsMap[li.perkId]) {
                    return li;
                }
                const { amount, recurringAmount } =
                    lineItemAmountsMap[li.perkId];
                return { ...li, amount, recurringAmount };
            });
            state.perkLineItems = updatedLineItems;
        },
        clearCart(state) {
            state.perkLineItems = [];
            state.amountLineItems = [];
        },
        clearAmountLineItems(state) {
            state.amountLineItems = [];
        },
        openPopup(state) {
            state.showPopup = true;
        },
        closePopup(state) {
            state.showPopup = false;
        },
        showCartValidationErrors(state, { payload: value }) {
            state.showValidationErrors = value;
        },
        setValidationErrors(state, { payload: errors }) {
            state.validationErrors = errors;
        },
    },
});

export const {
    updateAmountLineItem,
    removeAmountLineItems,
    addPerkLineItem,
    addPerk,
    addNewPerk,
    removePerk,
    removePerkLineItem,
    amountLineItems,
    updatePerkLineItemAmounts,
    clearCart,
    clearAmountLineItems,
    openPopup,
    closePopup,
    setValidationErrors,
    showCartValidationErrors,
} = cart.actions;

export const addAmountToCart = createAction(`${STATE_PREFIX}/addAmountToCart`);
export const replaceCart = createAction(`${STATE_PREFIX}/replaceCart`);
export const triggerValidation = createAction(
    `${STATE_PREFIX}/triggerValidation`,
);

export const cartSelector = state => state[STATE_PREFIX];

export const selectPerksPaymentOptionType = state =>
    fp.getOr(
        PERKS_PAYMENT_OPTIONS.SINGLE,
        'checkout.givingPaymentOptions.type',
        state,
    );

export const selectPerksDuration = state =>
    fp.getOr(0, 'checkout.givingPaymentOptions.duration', state);

export const selectPerkAmountProperty = createSelector(
    selectPerksPaymentOptionType,
    paymentOption =>
        paymentOption === PERKS_PAYMENT_OPTIONS.SINGLE
            ? 'amount'
            : 'recurringAmount',
);

export const selectAmountLineItems = createSelector(
    cartSelector,
    cartData => cartData.amountLineItems,
);

export const selectPerkLineItems = createSelector(
    cartSelector,
    cartData => cartData.perkLineItems,
);

export const selectSelectedPerkCounts = createSelector(
    selectPerkLineItems,
    perkLineItems =>
        fp.flow(
            fp.groupBy('perkId'),
            fp.mapValues(v => v[0].quantity),
        )(perkLineItems),
);

export const selectTotalPerksQuantity = createSelector(
    selectPerkLineItems,
    fp.sumBy('quantity'),
);

export const selectCartHasPerk = createSelector(cartSelector, cart =>
    Boolean(cart.perkLineItems.length),
);

export const selectAmountLineItemTotal = createSelector(
    cartSelector,
    cart => fp.sumBy('amount', cart.amountLineItems) || 0,
);

export const selectPerkLineItemTotal = createSelector(
    selectPerkLineItems,
    selectPerkAmountProperty,
    (perkLineItems, amountProp) =>
        fp.flow(
            fp.filter(perkLineItem =>
                fp.isNumber(fp.getOr(null, amountProp, perkLineItem)),
            ),
            fp.map(
                perkLineItem =>
                    perkLineItem[amountProp] * perkLineItem.quantity,
            ),
            fp.sum,
            total => (fp.isNumber(total) ? total : 0),
        )(perkLineItems),
);

export const selectCartTotal = createSelector(
    selectAmountLineItemTotal,
    selectPerkLineItemTotal,
    (amountLineItemsTotal, perkLineItemsTotal) =>
        amountLineItemsTotal + perkLineItemsTotal,
);

export const selectShowCartPopup = createSelector(
    cartSelector,
    ({ showPopup }) => showPopup,
);

export const selectShowCartValidationErrors = createSelector(
    cartSelector,
    cart => cart.showValidationErrors,
);

export const selectValidationErrors = createSelector(
    cartSelector,
    cart => cart.validationErrors,
);

export const selectAdditionalAmount = createSelector(
    selectAmountLineItems,
    amountLineItems =>
        amountLineItems.length ? amountLineItems[0].amount : null,
);

export const selectCartIsEmpty = createSelector(
    selectAmountLineItems,
    selectPerkLineItems,
    (amountLineItems, perkLineItems) =>
        amountLineItems.length === 0 && perkLineItems.length === 0,
);

export const selectCartHasUnspecifiedAmountPerk = createSelector(
    selectPerkLineItems,
    selectPerkAmountProperty,
    (perkLineItems, amountProperty) =>
        perkLineItems.some(item => {
            const primaryCurrencyAmountProp =
                amountProperty === 'amount'
                    ? 'primaryCurrencyAmount'
                    : 'primaryCurrencyRecurringAmount';
            return !item[primaryCurrencyAmountProp];
        }),
);
export default cart.reducer;
